import { Autocomplete, Box, Checkbox, Chip, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import { vulnerabilityKeys } from "../../../../api/queryKeys";
import { StyledTextField } from "../../../../components/forms/styles/formFieldStyles";
import LoadingIcon from "../../../../components/LoadingIcon";
import SvgToIcon from "../../../../icons/SvgToIcon";
import { icons } from "../../../stack/environment/icons/iconExport";

const formatFieldName = (fieldName) => {
  return fieldName
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const VulnerabilityFieldMultiSelect = ({
  selectedFields = [],
  onSelectionChange,
  label = "Vulnerability Fields",
}) => {
  const colors = useColors();
  const { getVulnerabilityFields } = useAPI();

  const { data: fieldsData, isLoading: isLoadingFields } = useQuery({
    queryKey: vulnerabilityKeys.fields(),
    queryFn: getVulnerabilityFields,
    select: (response) => response.data,
  });

  const fieldOptions = useMemo(() => {
    if (!fieldsData) return [];
    if (Array.isArray(fieldsData)) {
      return fieldsData.map((field) => ({
        value: field,
        label: formatFieldName(field),
        defaultSelected: false,
      }));
    }
    return Object.keys(fieldsData).map((field) => ({
      value: field,
      label: formatFieldName(field),
      defaultSelected: fieldsData[field],
    }));
  }, [fieldsData]);

  useEffect(() => {
    if (!fieldsData || Array.isArray(fieldsData) || selectedFields.length > 0) {
      return;
    }
    const defaultFields = Object.entries(fieldsData)
      .filter(([, isDefault]) => isDefault)
      .map(([field]) => field);
    if (defaultFields.length > 0) {
      onSelectionChange(defaultFields);
    }
  }, [fieldsData, selectedFields.length, onSelectionChange]);

  const selectedOptions = useMemo(
    () => fieldOptions.filter((opt) => selectedFields.includes(opt.value)),
    [fieldOptions, selectedFields]
  );

  const handleChange = (event, newValue) => {
    onSelectionChange(newValue.map((opt) => opt.value));
  };

  if (isLoadingFields) {
    return (
      <Box>
        <Typography
          variant="caption"
          sx={{ color: colors.text_secondary, mb: 1, display: "block" }}
        >
          {label}
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <LoadingIcon type="circular" size={16} />
          <Typography variant="caption">Loading fields...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Typography
        variant="caption"
        sx={{ color: colors.text_secondary, mb: 1, display: "block" }}
      >
        {label}
      </Typography>
      <Autocomplete
        multiple
        fullWidth
        size="small"
        disableCloseOnSelect
        limitTags={2}
        getLimitTagsText={(more) => `+${more}`}
        options={fieldOptions}
        value={selectedOptions}
        onChange={handleChange}
        getOptionLabel={(option) => option.label || ""}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        renderInput={(params) => (
          <StyledTextField
            {...params}
            placeholder={selectedOptions.length === 0 ? "Select fields" : ""}
            sx={{
              "& .MuiOutlinedInput-input": {
                fontSize: "14px",
                fontFamily: "Poppins, sans-serif",
                lineHeight: "14px",
                letterSpacing: "0.48px",
              },
            }}
          />
        )}
        renderOption={(props, option, { selected }) => (
          <li {...props} key={option.value}>
            <Checkbox
              checked={selected}
              sx={{ p: 0, mr: 1 }}
              icon={
                <SvgToIcon
                  IconLogo={icons.Unchecked}
                  style={{ width: "20px", height: "20px" }}
                />
              }
              checkedIcon={
                <SvgToIcon
                  IconLogo={icons.Checked}
                  style={{ width: "20px", height: "20px" }}
                />
              }
            />
            <Typography variant="caption" color={colors.text_primary}>
              {option.label}
            </Typography>
          </li>
        )}
        renderTags={(value, getTagProps) => {
          const maxTags = 3;
          const displayedTags = value.slice(0, maxTags);
          const extraCount = value.length - maxTags;

          return (
            <>
              {displayedTags.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.value}
                  label={option.label}
                  size="small"
                  sx={{
                    height: "24px",
                    "& .MuiChip-label": {
                      fontSize: "12px",
                    },
                  }}
                />
              ))}
              {extraCount > 0 && (
                <Typography variant="caption" sx={{ ml: 0.5 }}>
                  +{extraCount}
                </Typography>
              )}
            </>
          );
        }}
        ListboxProps={{
          sx: {
            maxHeight: "300px",
            "& .MuiAutocomplete-option": {
              minHeight: "auto",
              padding: "8px 16px",
            },
            '& .MuiAutocomplete-option[aria-selected="true"]': {
              backgroundColor: "transparent",
            },
            '& .MuiAutocomplete-option[aria-selected="true"].Mui-focused': {
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
          },
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            padding: "4px 9px",
            flexWrap: "nowrap !important",
            "& .MuiAutocomplete-input": {
              padding: "4px 4px",
              minWidth: "30px",
            },
            "& .MuiAutocomplete-tag": {
              maxWidth: "calc(50% - 6px)",
            },
          },
        }}
      />
    </Box>
  );
};

export default VulnerabilityFieldMultiSelect;
